import React, { useEffect } from "react";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaReact,
  FaCode,
  FaDatabase,
  FaMobileAlt,
  FaServer,
  FaGraduationCap,
  FaLaptopCode,
  FaPalette,
} from "react-icons/fa";
import { Link } from "react-router-dom";

// Course syllabus
const syllabus = [
  {
    title: "React Basics",
    img: "https://cdn-icons-png.flaticon.com/512/919/919851.png",
  },
  {
    title: "JSX & Components",
    img: "https://cdn-icons-png.flaticon.com/512/1828/1828970.png",
  },
  {
    title: "State & Props",
    img: "https://cdn-icons-png.flaticon.com/512/1828/1828843.png",
  },
  {
    title: "Hooks",
    img: "https://cdn-icons-png.flaticon.com/512/2910/2910764.png",
  },
  {
    title: "Routing & Navigation",
    img: "https://cdn-icons-png.flaticon.com/512/2910/2910731.png",
  },
  {
    title: "API Integration",
    img: "https://cdn-icons-png.flaticon.com/512/2910/2910757.png",
  },
  {
    title: "Project: Todo App",
    img: "https://cdn-icons-png.flaticon.com/512/2910/2910730.png",
  },
  {
    title: "Project: E-Commerce",
    img: "https://cdn-icons-png.flaticon.com/512/2910/2910732.png",
  },
];

// Topics
const topics = [
  {
    icon: <FaReact />,
    title: "Web Apps",
    desc: "Build interactive and dynamic web applications using React.",
  },
  {
    icon: <FaCode />,
    title: "Reusable Components",
    desc: "Write modular and reusable React components.",
  },
  {
    icon: <FaDatabase />,
    title: "State Management",
    desc: "Manage state efficiently with useState, useReducer, and Context API.",
  },
  {
    icon: <FaMobileAlt />,
    title: "React Native Basics",
    desc: "Learn how to create mobile apps using React Native.",
  },
  {
    icon: <FaPalette />,
    title: "Styling Components",
    desc: "Style React components using CSS, Tailwind, or styled-components.",
  },
];

// Extra Sections
const extraSections = [
  {
    icon: <FaReact />,
    title: "What is React JS?",
    desc: "React.js is a JavaScript library for building interactive user interfaces, primarily for single-page applications (SPAs).",
  },
  {
    icon: <FaGraduationCap />,
    title: "Why Learn React.js?",
    desc: "React.js is widely used for modern web development. Learning React improves job prospects and enables building fast SPAs.",
  },
  {
    icon: <FaLaptopCode />,
    title: "Career Opportunities",
    desc: "React developers are in high demand. Roles include Frontend Developer, Fullstack Developer, UI Engineer, and React Native Mobile Developer.",
  },
];

const gradients = [
  "linear-gradient(135deg, #dbeafe, #bfdbfe)", // blue
  "linear-gradient(135deg, #dcfce7, #bbf7d0)", // green
  "linear-gradient(135deg, #fde2ff, #fcd5fe)", // pink
];
const animationVariants = {
  bounce: { y: [0, -10, 0], transition: { repeat: Infinity, duration: 1 } },
  pulse: {
    scale: [1, 1.1, 1],
    transition: { repeat: Infinity, duration: 1.2 },
  },
  shake: {
    x: [0, -5, 5, -5, 5, 0],
    transition: { repeat: Infinity, duration: 0.8 },
  },
  spinSlow: {
    rotate: [0, 360],
    transition: { repeat: Infinity, duration: 4, ease: "linear" },
  },
  jump: { y: [0, -15, 0], transition: { repeat: Infinity, duration: 1 } },
};

export default function ReactAnimation() {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);
  const gradients = [
    "linear-gradient(135deg, #fef3c7, #fde68a)", // yellow
    "linear-gradient(135deg, #dbeafe, #bfdbfe)", // blue
    "linear-gradient(135deg, #dcfce7, #bbf7d0)", // green
    "linear-gradient(135deg, #ede9fe, #c4b5fd)", // purple
    "linear-gradient(135deg, #fee2e2, #fecaca)", // red
    "linear-gradient(135deg, #fef9c3, #fef08a)", // light yellow
    "linear-gradient(135deg, #cffafe, #a5f3fc)", // cyan
    "linear-gradient(135deg, #f5f3ff, #e0e7ff)", // indigo
  ];

  const gradient = [
    "linear-gradient(135deg, #dbeafe, #bfdbfe)", // blue
    "linear-gradient(135deg, #dcfce7, #bbf7d0)", // green
    "linear-gradient(135deg, #fde2ff, #fcd5fe)", // pink
    "linear-gradient(135deg, #ede9fe, #c4b5fd)", // purple
    "linear-gradient(135deg, #fef3c7, #fde68a)", // yellow
  ];

  return (
    <div
      style={{
        background: "#f5f5f5",
        color: "#222",
      }}
      className="overflow-hidden"
    >
      {/* Header */}
      <header
        style={{
          padding: "80px 20px",
          textAlign: "center",
          background:
            "linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('../assets/istockphoto-1442403794-170667a.jpg') center/cover no-repeat",
          color: "#fff",
        }}
      >
        <h1
          className="text-4xl md:text-6xl font-extrabold mb-4"
          data-aos="fade-down"
        >
          Learn <span style={{ color: "#61dafb" }}>React.js</span>
        </h1>

        <p
          data-aos="fade-up"
          style={{ marginTop: "10px" }}
          className="text-lg md:text-xl"
        >
          Build modern, responsive web applications with React.js and advance
          your career.
        </p>
        <Link
          to="/contact"
          className="mt-6 inline-block px-8 py-3 bg-blue-400 text-white rounded-full font-bold shadow-md hover:bg-blue-500 transition animate-bounce"
          data-aos="fade-up"
        >
          Enroll Now
        </Link>
      </header>
      {/* Extra Sections */}
      <section
        style={{ padding: "60px 20px", maxWidth: "1200px", margin: "0 auto" }}
      >
        {extraSections.map((section, i) => (
          <motion.div
            key={i}
            data-aos={i % 2 === 0 ? "fade-right" : "fade-left"}
            whileHover={{ scale: 1.05 }}
            style={{
              background: gradients[i % gradients.length],
              borderRadius: "15px",
              padding: "30px",
              marginBottom: "20px",
              boxShadow: "0 5px 20px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <motion.div
              animate={
                i % 2 === 0 ? { scale: [1, 1.1, 1] } : { y: [0, -5, 0, 5, 0] }
              }
              transition={{ duration: 2, repeat: Infinity, repeatType: "loop" }}
              style={{ fontSize: "3rem", color: "#1e3a8a" }}
            >
              {section.icon}
            </motion.div>
            <div>
              <h4 style={{ fontWeight: "bold" }}>{section.title}</h4>
              <p style={{ marginTop: "5px", color: "#555" }}>{section.desc}</p>
            </div>
          </motion.div>
        ))}
      </section>
      <section
        style={{ padding: "60px 20px", maxWidth: "1200px", margin: "0 auto" }}
      >
        <h2
          data-aos="fade-up"
          style={{
            textAlign: "center",
            marginBottom: "40px",
            fontSize: "2.5rem",
            fontWeight: "bold",
          }}
        >
          Course Syllabus
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "20px",
          }}
        >
          {syllabus.map((module, i) => (
            <motion.div
              key={i}
              data-aos={i % 2 === 0 ? "flip-left" : "flip-right"}
              data-aos-delay={i * 100}
              whileHover={{ scale: 1.05 }}
              style={{
                background: gradients[i % gradients.length],
                borderRadius: "20px",
                padding: "20px",
                boxShadow: "0 5px 20px rgba(0,0,0,0.1)",
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              <motion.img
                src={module.img}
                alt={module.title}
                style={{
                  width: "80px",
                  marginBottom: "15px",
                  borderRadius: "10px",
                }}
                animate={
                  i % 4 === 0
                    ? animationVariants.bounce
                    : i % 4 === 1
                    ? animationVariants.pulse
                    : i % 4 === 2
                    ? animationVariants.jump
                    : animationVariants.shake
                }
              />
              <h4 style={{ fontWeight: "bold" }}>{module.title}</h4>
            </motion.div>
          ))}
        </div>
      </section>
      {/* Topics */}
      <section
        style={{ padding: "60px 20px", maxWidth: "1200px", margin: "0 auto" }}
      >
        <h2
          data-aos="fade-up"
          style={{
            textAlign: "center",
            marginBottom: "40px",
            fontSize: "2.5rem",
            fontWeight: "bold",
          }}
        >
          What You Will Learn
        </h2>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "20px",
          }}
        >
          {topics.map((topic, i) => (
            <motion.div
              key={i}
              className="topic-card"
              data-aos={i % 2 === 0 ? "flip-left" : "flip-right"}
              data-aos-delay={i * 100}
              whileHover={{ scale: 1.05, rotate: i % 2 === 0 ? 2 : -2 }}
              style={{
                background: gradient[i % gradient.length],
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 5px 20px rgba(0,0,0,0.1)",
                minWidth: "250px",
                flex: "1 1 250px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "15px",
              }}
            >
              <motion.div
                animate={{
                  rotate: [0, 10, -10, 0],
                  y: [0, -5, 0, 5, 0],
                  scale: [1, 1.2, 1, 1.2, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "loop",
                  ease: "easeInOut",
                }}
                style={{ fontSize: "2rem", color: "#1e3a8a" }}
              >
                {topic.icon}
              </motion.div>
              <div>
                <h4 style={{ margin: 0, fontWeight: "bold" }}>{topic.title}</h4>
                <p style={{ margin: "5px 0", color: "#555" }}>{topic.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <footer
        style={{
          padding: "40px 20px",
          textAlign: "center",
          background: "#222",
          color: "#fff",
        }}
      >
        <h3 style={{ fontWeight: "bold", marginBottom: "15px" }}>
          🚀 Start Learning React.js Today!
        </h3>
        <Link
          to="/contact"
          className="inline-block mt-3 px-8 py-3 bg-blue-400 rounded-full font-bold shadow-md hover:bg-blue-500 transition animate-bounce"
        >
          Enroll Now
        </Link>
      </footer>
    </div>
  );
}
